import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ClearableInput } from '../components/ui/clearable-input';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { useLanguage } from '../contexts/LanguageContext';
import { Target, Plus, Check, Trash2, Sparkles, TrendingUp, X, Loader2, Pencil, RefreshCw, CalendarCheck, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const NANOUK_ICON = 'https://images.unsplash.com/photo-1767381604151-bae00f2fb337?w=80&h=80&fit=crop';

export default function Goals() {
    const { language } = useLanguage();
    const [activeGoals, setActiveGoals] = useState([]);
    const [suggestions, setSuggestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAdd, setShowAdd] = useState(false);
    const [newGoal, setNewGoal] = useState({ title: '', category: 'custom', target_value: '', target_unit: 'kg', timeframe: 'weekly' });
    const [editingId, setEditingId] = useState(null);
    const [editDraft, setEditDraft] = useState({ title: '', target_value: '', target_unit: 'kg', timeframe: 'weekly' });
    const [comments, setComments] = useState({}); // { goalId: { text, loading } }
    const [autoLoaded, setAutoLoaded] = useState(false);
    const [briefing, setBriefing] = useState(null);
    const [briefingLoading, setBriefingLoading] = useState(false);
    const [briefingExpanded, setBriefingExpanded] = useState(true);
    const t = language === 'de';

    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only loaders
    useEffect(() => { loadData(); loadBriefing(false); }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const [goalsRes, sugRes] = await Promise.all([
                axios.get(`${API}/api/goals/active`),
                axios.get(`${API}/api/goals/suggestions`)
            ]);
            const goals = goalsRes.data?.goals || [];
            setActiveGoals(goals);
            setSuggestions(sugRes.data?.suggestions || []);
            // Seed initial comments from cached values
            const seeded = {};
            goals.forEach(g => {
                if (g.nanouk_comment) seeded[g.id] = { text: g.nanouk_comment, loading: false };
            });
            setComments(seeded);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    };

    // After goals load: auto-fetch Nanouk comments for goals without cached one (only first run)
    useEffect(() => {
        if (loading || autoLoaded || activeGoals.length === 0) return;
        setAutoLoaded(true);
        activeGoals.forEach(g => {
            if (!g.nanouk_comment) {
                fetchComment(g.id, false);
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, activeGoals]);

    const fetchComment = async (goalId, refresh) => {
        setComments(prev => ({ ...prev, [goalId]: { ...(prev[goalId] || {}), loading: true } }));
        try {
            const res = await axios.post(`${API}/api/goals/${goalId}/nanouk-comment${refresh ? '?refresh=true' : ''}`, {});
            setComments(prev => ({ ...prev, [goalId]: { text: res.data?.comment || '', loading: false } }));
        } catch (e) {
            setComments(prev => ({ ...prev, [goalId]: { text: '', loading: false } }));
        }
    };

    const loadBriefing = async (refresh) => {
        setBriefingLoading(true);
        try {
            const res = await axios.get(`${API}/api/goals/weekly-briefing?language=${language}${refresh ? '&refresh=true' : ''}`);
            setBriefing(res.data);
        } catch (err) { console.warn('weekly briefing load failed:', err); }
        finally { setBriefingLoading(false); }
    };

    const acceptSuggestion = async (s) => {
        try {
            await axios.post(`${API}/api/goals/accept`, {
                title: s.title, category: s.category, target_value: s.target_value,
                target_unit: s.target_unit, timeframe: s.timeframe
            });
            toast.success(t ? 'Ziel angenommen!' : 'Goal accepted!');
            setAutoLoaded(false);
            loadData();
        } catch (e) { toast.error('Error'); }
    };

    const createGoal = async () => {
        if (!newGoal.title.trim()) return;
        try {
            await axios.post(`${API}/api/goals/create`, {
                ...newGoal,
                target_value: newGoal.target_value ? parseFloat(newGoal.target_value) : null
            });
            toast.success(t ? 'Ziel erstellt!' : 'Goal created!');
            setNewGoal({ title: '', category: 'custom', target_value: '', target_unit: 'kg', timeframe: 'weekly' });
            setShowAdd(false);
            setAutoLoaded(false);
            loadData();
        } catch (e) { toast.error('Error'); }
    };

    const deleteGoal = async (id) => {
        try {
            await axios.delete(`${API}/api/goals/${id}`);
            loadData();
        } catch (e) { toast.error('Error'); }
    };

    const startEdit = (goal) => {
        setEditingId(goal.id);
        setEditDraft({
            title: goal.title || '',
            target_value: goal.target_value != null ? String(goal.target_value) : '',
            target_unit: goal.target_unit || 'kg',
            timeframe: goal.timeframe || 'weekly'
        });
    };

    const cancelEdit = () => {
        setEditingId(null);
    };

    const saveEdit = async (goalId) => {
        if (!editDraft.title.trim()) {
            toast.error(t ? 'Titel darf nicht leer sein' : 'Title required');
            return;
        }
        try {
            await axios.put(`${API}/api/goals/${goalId}`, {
                title: editDraft.title.trim(),
                target_value: editDraft.target_value ? parseFloat(editDraft.target_value) : null,
                target_unit: editDraft.target_unit,
                timeframe: editDraft.timeframe
            });
            toast.success(t ? 'Ziel aktualisiert' : 'Goal updated');
            setEditingId(null);
            // Reset Nanouk comment since key fields may have changed
            setComments(prev => ({ ...prev, [goalId]: { text: '', loading: false } }));
            setAutoLoaded(false);
            loadData();
        } catch (e) { toast.error('Error'); }
    };

    if (loading) return <div className="p-4"><div className="h-64 bg-muted animate-pulse rounded-xl" /></div>;

    return (
        <div className="p-4 lg:p-8 pb-24 lg:pb-8" data-testid="goals-page">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-4xl mx-auto space-y-4">
                <div className="flex items-center justify-between">
                    <h1 className="font-display text-2xl font-bold flex items-center gap-2">
                        <Target className="w-6 h-6 text-primary" />
                        {t ? 'Meine Ziele' : 'My Goals'}
                    </h1>
                    <Button size="sm" className="rounded-full gap-1" onClick={() => setShowAdd(!showAdd)} data-testid="add-custom-goal-btn">
                        <Plus className="w-4 h-4" /> {t ? 'Eigenes' : 'Custom'}
                    </Button>
                </div>

                {/* Weekly Briefing Banner (auto-refreshed every Monday) */}
                {(briefingLoading || briefing) && (
                    <Card className="border-2 border-primary/30 bg-gradient-to-br from-primary/5 via-background to-amber-50/40 dark:from-primary/10 dark:to-amber-950/10 overflow-hidden" data-testid="weekly-briefing-card">
                        <CardContent className="p-4">
                            <button
                                onClick={() => setBriefingExpanded(v => !v)}
                                className="w-full flex items-center justify-between gap-2 text-left"
                                data-testid="briefing-toggle-btn"
                            >
                                <div className="flex items-center gap-2 min-w-0">
                                    <div className="w-8 h-8 rounded-lg bg-primary/15 flex items-center justify-center shrink-0">
                                        <CalendarCheck className="w-4 h-4 text-primary" />
                                    </div>
                                    <div className="min-w-0">
                                        <p className="font-semibold text-sm leading-tight">
                                            {t ? 'Nanouks Wochen-Briefing' : "Nanouk's weekly briefing"}
                                        </p>
                                        {briefing?.week_of && (
                                            <p className="text-[10px] text-muted-foreground">
                                                {t ? 'Woche ab' : 'Week of'} {new Date(briefing.week_of).toLocaleDateString(t ? 'de-DE' : 'en-GB', { day: '2-digit', month: 'short' })}
                                                {briefing.cached ? '' : ` · ${t ? 'neu' : 'new'}`}
                                            </p>
                                        )}
                                    </div>
                                </div>
                                {briefingExpanded ? <ChevronUp className="w-4 h-4 text-muted-foreground shrink-0" /> : <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />}
                            </button>

                            <AnimatePresence initial={false}>
                                {briefingExpanded && (
                                    <motion.div
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: 'auto' }}
                                        exit={{ opacity: 0, height: 0 }}
                                        className="overflow-hidden"
                                    >
                                        <div className="mt-3 space-y-3">
                                            {briefingLoading && !briefing ? (
                                                <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                                    {t ? 'Nanouk erstellt dein Briefing...' : 'Nanouk is preparing your briefing...'}
                                                </p>
                                            ) : (
                                                <>
                                                    <p className="text-sm leading-relaxed text-foreground/90" data-testid="briefing-text">
                                                        {briefing?.briefing}
                                                    </p>
                                                    {(briefing?.highlights?.length > 0 || briefing?.focus?.length > 0) && (
                                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                                            {briefing?.highlights?.length > 0 && (
                                                                <div className="p-2 rounded-lg bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-900">
                                                                    <p className="text-[10px] font-semibold text-green-700 dark:text-green-300 uppercase tracking-wide mb-1">
                                                                        {t ? 'Läuft gut' : 'On track'}
                                                                    </p>
                                                                    <ul className="space-y-0.5">
                                                                        {briefing.highlights.map(h => (
                                                                            <li key={h.id} className="text-xs text-foreground/80 flex justify-between gap-2">
                                                                                <span className="truncate">{h.title}</span>
                                                                                <span className="text-green-600 font-semibold shrink-0">{h.progress}%</span>
                                                                            </li>
                                                                        ))}
                                                                    </ul>
                                                                </div>
                                                            )}
                                                            {briefing?.focus?.length > 0 && (
                                                                <div className="p-2 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900">
                                                                    <p className="text-[10px] font-semibold text-amber-700 dark:text-amber-300 uppercase tracking-wide mb-1">
                                                                        {t ? 'Fokus diese Woche' : 'Focus this week'}
                                                                    </p>
                                                                    <ul className="space-y-0.5">
                                                                        {briefing.focus.map(f => (
                                                                            <li key={f.id} className="text-xs text-foreground/80 flex justify-between gap-2">
                                                                                <span className="truncate">{f.title}</span>
                                                                                <span className="text-amber-600 font-semibold shrink-0">{f.progress}%</span>
                                                                            </li>
                                                                        ))}
                                                                    </ul>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                    <div className="flex items-center justify-end">
                                                        <button
                                                            onClick={() => loadBriefing(true)}
                                                            disabled={briefingLoading}
                                                            className="text-[11px] text-primary hover:underline flex items-center gap-1 disabled:opacity-50"
                                                            data-testid="briefing-refresh-btn"
                                                        >
                                                            <RefreshCw className={`w-3 h-3 ${briefingLoading ? 'animate-spin' : ''}`} />
                                                            {t ? 'Neu generieren' : 'Regenerate'}
                                                        </button>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </CardContent>
                    </Card>
                )}

                {/* Add custom goal */}
                {showAdd && (
                    <Card className="border-2 border-primary/20">
                        <CardContent className="p-4 space-y-3">
                            <p className="font-semibold text-sm">{t ? 'Eigenes Ziel erstellen' : 'Create Custom Goal'}</p>
                            <ClearableInput value={newGoal.title} onChange={(e) => setNewGoal({...newGoal, title: e.target.value})}
                                placeholder={t ? 'z.B. Zielgewicht 68kg, 5km Lauf unter 25min' : 'e.g. Target weight 68kg'}
                                className="bg-muted border-2 border-border" data-testid="new-goal-title-input" />
                            <div className="flex gap-2">
                                <select value={newGoal.category} onChange={(e) => setNewGoal({...newGoal, category: e.target.value})}
                                    className="h-9 px-2 rounded-md border border-input bg-background text-xs flex-1">
                                    <option value="weight">{t ? 'Gewicht' : 'Weight'}</option>
                                    <option value="muscle">{t ? 'Muskeln' : 'Muscle'}</option>
                                    <option value="fitness">Fitness</option>
                                    <option value="nutrition">{t ? 'Ernährung' : 'Nutrition'}</option>
                                    <option value="sleep">{t ? 'Schlaf' : 'Sleep'}</option>
                                    <option value="mental">Mental</option>
                                    <option value="custom">{t ? 'Sonstiges' : 'Other'}</option>
                                </select>
                                <Input value={newGoal.target_value} onChange={(e) => setNewGoal({...newGoal, target_value: e.target.value})}
                                    placeholder={t ? 'Zielwert' : 'Target'} type="number" className="w-20 bg-muted border-2 border-border" />
                                <select value={newGoal.target_unit} onChange={(e) => setNewGoal({...newGoal, target_unit: e.target.value})}
                                    className="h-9 px-2 rounded-md border border-input bg-background text-xs w-16">
                                    <option value="kg">kg</option>
                                    <option value="g">g</option>
                                    <option value="min">min</option>
                                    <option value="x">x</option>
                                    <option value="km">km</option>
                                    <option value="h">h</option>
                                    <option value="%">%</option>
                                </select>
                            </div>
                            <div className="flex gap-2">
                                <select value={newGoal.timeframe} onChange={(e) => setNewGoal({...newGoal, timeframe: e.target.value})}
                                    className="h-9 px-2 rounded-md border border-input bg-background text-xs flex-1">
                                    <option value="daily">{t ? 'Täglich' : 'Daily'}</option>
                                    <option value="weekly">{t ? 'Wöchentlich' : 'Weekly'}</option>
                                    <option value="monthly">{t ? 'Monatlich' : 'Monthly'}</option>
                                </select>
                                <Button onClick={createGoal} disabled={!newGoal.title.trim()} className="gap-1" data-testid="create-goal-btn">
                                    <Check className="w-4 h-4" /> {t ? 'Erstellen' : 'Create'}
                                </Button>
                                <Button variant="ghost" size="icon" onClick={() => setShowAdd(false)}>
                                    <X className="w-4 h-4" />
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Active Goals */}
                {activeGoals.length > 0 && (
                    <div className="space-y-2">
                        <p className="text-sm font-semibold flex items-center gap-2">
                            <TrendingUp className="w-4 h-4 text-primary" />
                            {t ? 'Aktive Ziele' : 'Active Goals'} ({activeGoals.length})
                        </p>
                        {activeGoals.map((goal) => {
                            const isEditing = editingId === goal.id;
                            const cmt = comments[goal.id];
                            return (
                                <Card key={goal.id} className={`border ${goal.progress >= 100 ? 'border-green-300 bg-green-50 dark:bg-green-950/20' : ''}`} data-testid={`goal-card-${goal.id}`}>
                                    <CardContent className="p-4">
                                        {isEditing ? (
                                            <div className="space-y-2">
                                                <Input
                                                    value={editDraft.title}
                                                    onChange={(e) => setEditDraft({...editDraft, title: e.target.value})}
                                                    className="bg-muted border-2 border-border text-sm"
                                                    placeholder={t ? 'Titel' : 'Title'}
                                                    data-testid={`edit-title-${goal.id}`}
                                                />
                                                <div className="flex gap-2">
                                                    <Input
                                                        type="number"
                                                        value={editDraft.target_value}
                                                        onChange={(e) => setEditDraft({...editDraft, target_value: e.target.value})}
                                                        placeholder={t ? 'Zielwert' : 'Target'}
                                                        className="w-24 bg-muted border-2 border-border text-sm"
                                                        data-testid={`edit-target-${goal.id}`}
                                                    />
                                                    <select
                                                        value={editDraft.target_unit}
                                                        onChange={(e) => setEditDraft({...editDraft, target_unit: e.target.value})}
                                                        className="h-9 px-2 rounded-md border border-input bg-background text-xs w-20"
                                                    >
                                                        <option value="kg">kg</option>
                                                        <option value="g">g</option>
                                                        <option value="ml">ml</option>
                                                        <option value="min">min</option>
                                                        <option value="x">x</option>
                                                        <option value="km">km</option>
                                                        <option value="h">h</option>
                                                        <option value="%">%</option>
                                                        <option value="steps">steps</option>
                                                        <option value="kcal">kcal</option>
                                                    </select>
                                                    <select
                                                        value={editDraft.timeframe}
                                                        onChange={(e) => setEditDraft({...editDraft, timeframe: e.target.value})}
                                                        className="h-9 px-2 rounded-md border border-input bg-background text-xs flex-1"
                                                    >
                                                        <option value="daily">{t ? 'Täglich' : 'Daily'}</option>
                                                        <option value="weekly">{t ? 'Wöchentlich' : 'Weekly'}</option>
                                                        <option value="monthly">{t ? 'Monatlich' : 'Monthly'}</option>
                                                    </select>
                                                </div>
                                                <div className="flex gap-2 justify-end">
                                                    <Button size="sm" variant="ghost" onClick={cancelEdit} data-testid={`cancel-edit-${goal.id}`}>
                                                        <X className="w-4 h-4" />
                                                    </Button>
                                                    <Button size="sm" onClick={() => saveEdit(goal.id)} className="gap-1" data-testid={`save-edit-${goal.id}`}>
                                                        <Check className="w-4 h-4" /> {t ? 'Speichern' : 'Save'}
                                                    </Button>
                                                </div>
                                            </div>
                                        ) : (
                                            <>
                                                <div className="flex items-start justify-between gap-2">
                                                    <div className="flex-1 min-w-0">
                                                        <p className="font-medium text-sm">{goal.title}</p>
                                                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                                                            <Badge variant="outline" className="text-[9px]">{goal.category}</Badge>
                                                            <span className="text-xs text-muted-foreground">{goal.current}</span>
                                                            {goal.target_value != null && (
                                                                <span className="text-[10px] text-muted-foreground">
                                                                    {t ? 'Ziel:' : 'Goal:'} {goal.target_value}{goal.target_unit || ''}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-2 shrink-0">
                                                        <span className={`text-lg font-bold ${goal.progress >= 100 ? 'text-green-600' : goal.progress >= 60 ? 'text-primary' : 'text-muted-foreground'}`}>
                                                            {goal.progress}%
                                                        </span>
                                                        <button onClick={() => startEdit(goal)} className="p-1 opacity-50 hover:opacity-100" title={t ? 'Bearbeiten' : 'Edit'} data-testid={`edit-goal-${goal.id}`}>
                                                            <Pencil className="w-3.5 h-3.5" />
                                                        </button>
                                                        <button onClick={() => deleteGoal(goal.id)} className="p-1 opacity-50 hover:opacity-100" data-testid={`delete-goal-${goal.id}`}>
                                                            <Trash2 className="w-3.5 h-3.5" />
                                                        </button>
                                                    </div>
                                                </div>
                                                <Progress value={goal.progress} className="h-2 mt-2" />
                                                {goal.progress >= 100 && <p className="text-xs text-green-600 mt-1 font-medium">{t ? '✓ Ziel erreicht!' : '✓ Goal achieved!'}</p>}

                                                {/* Nanouk Empfehlung */}
                                                <AnimatePresence>
                                                    {(cmt?.loading || cmt?.text) && (
                                                        <motion.div
                                                            initial={{ opacity: 0, height: 0 }}
                                                            animate={{ opacity: 1, height: 'auto' }}
                                                            exit={{ opacity: 0, height: 0 }}
                                                            className="mt-3 overflow-hidden"
                                                        >
                                                            <div className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900">
                                                                <img src={NANOUK_ICON} alt="Nanouk" className="w-7 h-7 rounded-lg object-cover shrink-0 mt-0.5" />
                                                                <div className="flex-1 min-w-0">
                                                                    {cmt?.loading ? (
                                                                        <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                                                                            <Loader2 className="w-3 h-3 animate-spin" />
                                                                            {t ? 'Nanouk überlegt...' : 'Nanouk thinking...'}
                                                                        </p>
                                                                    ) : (
                                                                        <p className="text-xs leading-relaxed text-foreground/90" data-testid={`nanouk-comment-${goal.id}`}>
                                                                            {cmt.text}
                                                                        </p>
                                                                    )}
                                                                </div>
                                                                {!cmt?.loading && (
                                                                    <button
                                                                        onClick={() => fetchComment(goal.id, true)}
                                                                        className="p-1 opacity-60 hover:opacity-100 shrink-0"
                                                                        title={t ? 'Neue Empfehlung' : 'Refresh'}
                                                                        data-testid={`refresh-comment-${goal.id}`}
                                                                    >
                                                                        <RefreshCw className="w-3 h-3" />
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </motion.div>
                                                    )}
                                                </AnimatePresence>

                                                {!cmt?.text && !cmt?.loading && (
                                                    <button
                                                        onClick={() => fetchComment(goal.id, false)}
                                                        className="mt-2 text-[11px] text-amber-600 dark:text-amber-400 hover:underline flex items-center gap-1"
                                                        data-testid={`ask-nanouk-${goal.id}`}
                                                    >
                                                        <Sparkles className="w-3 h-3" />
                                                        {t ? 'Nanouk fragen' : 'Ask Nanouk'}
                                                    </button>
                                                )}
                                            </>
                                        )}
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>
                )}

                {/* Suggestions from Nanouk */}
                {suggestions.length > 0 && (
                    <div className="space-y-2">
                        <p className="text-sm font-semibold flex items-center gap-2">
                            <Sparkles className="w-4 h-4 text-amber-500" />
                            {t ? 'Nanouk schlägt vor' : 'Nanouk suggests'}
                        </p>
                        {suggestions.filter(s => !activeGoals.some(g => g.category === s.category && g.target_value === s.target_value)).slice(0, 5).map((s) => (
                            <Card key={`sug-${s.category}-${s.title}`} className="border border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-950/10">
                                <CardContent className="p-3">
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium text-sm flex items-center gap-1.5">
                                                <span>{s.emoji}</span> {s.title}
                                            </p>
                                            <p className="text-xs text-muted-foreground mt-0.5">{s.reason}</p>
                                        </div>
                                        <Button size="sm" variant="outline" className="shrink-0 h-7 text-xs rounded-full border-amber-300 hover:bg-amber-100"
                                            onClick={() => acceptSuggestion(s)}>
                                            <Plus className="w-3 h-3 mr-1" /> {t ? 'Annehmen' : 'Accept'}
                                        </Button>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}

                {activeGoals.length === 0 && suggestions.length === 0 && (
                    <div className="text-center py-12">
                        <Target className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                        <p className="text-muted-foreground">{t ? 'Tracke mehr Daten — Nanouk schlägt dann Ziele vor!' : 'Track more data — Nanouk will suggest goals!'}</p>
                    </div>
                )}
            </motion.div>
        </div>
    );
}
